'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn, formatPrice } from '@/lib/utils';
import { BookOpen, Users, Star } from 'lucide-react';
import { subjectIcons, subjectLabels, formatGradeLevels } from '@/lib/course-display';
import { MidTermBadge } from './mid-term-badge';
import type { PublicCourseWithMidTerm } from '@/server/services/course.service';

export type AvailabilityStatus = 'available' | 'limited' | 'full';

export type CourseCatalogCardProps = {
  course: PublicCourseWithMidTerm;
  availabilityStatus?: AvailabilityStatus;
  isAuthenticated?: boolean;
  className?: string;
};

export function CourseCatalogCard({
  course,
  availabilityStatus = 'available',
  isAuthenticated = false,
  className,
}: CourseCatalogCardProps) {
  const t = useTranslations('public.courseCard');
  const SubjectIcon = subjectIcons[course.subject] ?? BookOpen;
  const gradeRange = formatGradeLevels(course.gradeLevels);

  return (
    <Card
      className={cn(
        'relative flex flex-col justify-between overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg',
        className,
      )}
    >
      <div className="relative -mt-6 aspect-[16/9] w-full overflow-hidden rounded-t-xl">
        {course.thumbnailUrl ? (
          <img
            src={course.thumbnailUrl}
            alt={t('thumbnailAlt', { title: course.title })}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            role="img"
            aria-label={t('noThumbnailAlt', { title: course.title })}
            className="flex h-full w-full items-center justify-center bg-gradient-to-br from-eifo-primary/10 to-eifo-primary/5"
          >
            <SubjectIcon className="h-12 w-12 text-eifo-primary/40" aria-hidden="true" />
          </div>
        )}
      </div>
      <CardHeader>
        <div className="flex items-center gap-2">
          <SubjectIcon className="h-5 w-5 text-eifo-primary" aria-hidden="true" />
          <Badge variant="secondary" className="text-xs">
            {subjectLabels[course.subject] ?? course.subject}
          </Badge>
          {availabilityStatus === 'limited' && (
            <Badge variant="outline" className="border-orange-400 text-orange-600 text-xs">
              {t('limitedSeats')}
            </Badge>
          )}
          {availabilityStatus === 'full' && (
            <Badge variant="outline" className="border-red-400 text-red-600 text-xs">
              {t('full')}
            </Badge>
          )}
        </div>
        <CardTitle className="text-lg text-eifo-primary">
          {/* Stretched link: the ::before overlay makes the WHOLE card
              navigate to the course; the footer CTA sits above it (z-10). */}
          <Link
            href={isAuthenticated ? `/catalog/${course.slug}` : `/courses/${course.slug}`}
            className="hover:underline before:absolute before:inset-0 before:z-[1] before:content-['']"
          >
            {course.title}
          </Link>
        </CardTitle>
        {course.midTermAvailability && (
          <MidTermBadge
            midTermAvailability={course.midTermAvailability}
            showPrice={isAuthenticated}
          />
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground line-clamp-2">
          {course.description.replace(/<[^>]*>/g, '')}
        </p>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          {gradeRange && (
            <span className="font-medium text-eifo-primary">{gradeRange}</span>
          )}
          <span className="flex items-center gap-1 text-muted-foreground">
            <Users className="h-4 w-4" aria-hidden="true" />
            {t('maxStudents', { count: course.maxClassSize })}
          </span>
          {/* Aggregate rating — hidden at 0 reviews to avoid implying "0 stars". */}
          {course.reviewCount > 0 && course.reviewAverage !== null && (
            <span
              className="flex items-center gap-1 font-medium text-eifo-primary"
              aria-label={t('ratingAria', {
                rating: course.reviewAverage.toFixed(1),
                count: course.reviewCount,
              })}
            >
              <Star className="h-4 w-4 fill-eifo-orange text-eifo-orange" aria-hidden="true" />
              {course.reviewAverage.toFixed(1)} ({course.reviewCount})
            </span>
          )}
        </div>
        {/* Price is hidden on public surfaces (logged-out catalog + homepage);
            it shows only for authenticated parents and on the detail page. */}
        {isAuthenticated && (
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <span>{t('perTerm', { price: formatPrice(course.pricePerTermCents) })}</span>
          </div>
        )}
      </CardContent>
      <CardFooter>
        {availabilityStatus === 'full' ? (
          <Button
            asChild
            variant="outline"
            className="relative z-10 min-h-[44px] min-w-[44px] w-full"
          >
            <Link href={isAuthenticated ? `/catalog/${course.slug}` : `/register?redirect=/courses/${course.slug}`}>
              {isAuthenticated ? t('viewCourse') : t('joinWaitlist')}
            </Link>
          </Button>
        ) : (
          <Button
            asChild
            className="relative z-10 min-h-[44px] min-w-[44px] w-full bg-eifo-orange hover:bg-eifo-orange/85 text-[var(--eifo-primary)] font-semibold"
          >
            <Link href={isAuthenticated ? `/catalog/${course.slug}` : `/courses/${course.slug}`}>
              {isAuthenticated ? t('enrollNow') : t('registerToEnroll')}
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
